import { useState } from 'react';
import { Save, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/dialog/ConfirmDialog';
import { FormDialog } from '@/components/dialog/FormDialog';
import { ReferenceOptions } from '@/components/form/ReferenceOptions';
import { SelectField } from '@/components/form/SelectField';
import { useSubmit } from '@/hooks/useSubmit';
import MemberModel from '@/models/entities/MemberModel';
import { resolveReference, useReferenceOptions } from '@/stores/useReferenceOptions';

/** Retrait d'un user d'une société (DELETE /members/{id}) par un Dirigeant ; il quitte aussi son équipe */
export function RemoveMembershipDialog({
  personName,
  member,
  onClose,
  onRemoved,
}: {
  /** NOM Prénom de la personne retirée */
  personName: string;
  member: MemberModel;
  onClose: () => void;
  onRemoved: () => void;
}) {
  return (
    <ConfirmDialog
      title={`Retirer ${personName} de ${member.companyName} ?`}
      description={`Il n'apparaîtra plus parmi les employés de ${member.companyName} ni dans ses équipes. Son compte n'est pas supprimé.`}
      confirmLabel="Retirer"
      confirmIcon={Trash2}
      destructive
      onConfirm={() => new MemberModel({ id: member.id }).destroy()}
      onDone={onRemoved}
      onClose={onClose}
    />
  );
}

/** Changement du rôle dans la société (PATCH /members/{id} : seul companyRole est modifiable) */
export function ChangeCompanyRoleDialog({
  personName,
  member,
  onClose,
  onSaved,
}: {
  personName: string;
  member: MemberModel;
  onClose: () => void;
  onSaved: (roleLabel: string) => void;
}) {
  const [companyRole, setCompanyRole] = useState<string>(member.companyRoleReference);
  const roleOptions = useReferenceOptions('companyRoles');
  const { submit, submitting, error } = useSubmit();

  const save = () =>
    submit(async () => {
      await new MemberModel({ id: member.id }).save(
        { companyRole: (await resolveReference('companyRoles', companyRole)).urn },
        { silentError: true },
      );
      onSaved(roleOptions.find((option) => option.reference === companyRole)?.label ?? '');
    });

  return (
    <FormDialog
      title={`Changer le rôle de ${personName}`}
      description={`Chez ${member.companyName}.`}
      submitLabel="Enregistrer"
      submitIcon={Save}
      canSubmit={companyRole !== member.companyRoleReference}
      submitting={submitting}
      error={error}
      onSubmit={save}
      onClose={onClose}
    >
      <SelectField
        id="member-role"
        label="Rôle dans la société"
        required
        value={companyRole}
        onChange={(e) => setCompanyRole(e.target.value)}
      >
        <ReferenceOptions name="companyRoles" />
      </SelectField>
    </FormDialog>
  );
}
